import { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import showNotification, {
  dismissNotification,
} from "components/Notifications/Notifications";
import interpreter_icon from "assets/images/buttons/ic_interpreter.svg";
import {
  setInterpreterEstimateTime,
  setInterpreterRequestID,
  setInterpreterRequestStatus,
} from "store/actions/interpreter";
import {
  getInterpreterProvider,
  setURL,
} from "../providers/InterpreterProvider";
import { useTranslation } from "react-i18next";
import {
  interpreterInviteSend,
  interpreterInviteFail,
} from "store/actions/googleAnalytics";

const useInterpreterPolling = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const interpreterSessionId = useSelector(
    (state) => state.interpreter.interpreterSessionID
  );
  const estimatedTime = useSelector((state) => state.interpreter.estimatedTime);
  const isInterpreterRequestInProgress = useSelector(
    (state) => state.interpreter.interpreterRequestIsInProgress
  );
  const isEstimatedTimeAvailable = useSelector(
    (state) => state.interpreter.isEstimatedTimeAvailable
  );
  const interpreterSelectedLanguage = useSelector(
    (state) => state.interpreter.selectedLanguage
  );
  const interpreterSelectedGender = useSelector(
    (state) => state.interpreter.selectedGender
  );

  const interpreterCallType = useSelector(
    (state) => state.interpreter.callType
  );

  const roomPin = useSelector(
    (state) => state.user.userInfo?.RoomMode?.roomPIN
  );

  const isRoomPin = useSelector(
    (state) => state.user.userInfo?.RoomMode?.hasPIN
  );

  const portalURL = useSelector((state) => state.config.urlPortal.value);

  const roomKey = useSelector((state) => state.config.urlRoomKey.value);

  const particpantList = useSelector((state) => state.call.participants);

  const interpreterProvider = useRef(null);
  const interpreterCurrentSessionId = useRef(null);

  const gcpServices = useSelector((state) => state.config.listOfGCPServices);

  const pollingTimer = useRef(null);
  const inviteNotification = useRef(null);
  const interpreterRetryInviteNotification = useRef(null);
  const interpreterLeftTimer = useRef(null);
  const interpreRequestStatus = useRef(null);
  const INTERPRETER_STATUS = {
    PENDING: "0",
    JOINED: "1",
    COMPLETED: "2",
    CANCELLED: "3",
  };

  const sendInvite = async () => {
    const resp = await interpreterProvider.current.sendInvite({
      languageId: interpreterSelectedLanguage.value,
      conferencType: interpreterCallType,
      genderOption: interpreterSelectedGender.value,
      meetingRoomId: roomKey,
      meetingPortal: portalURL,
      meetingRoomPin: isRoomPin ? roomPin : "",
    });
    if (resp.status) {
      localStorage.setItem(
        "interpreterSessionId",
        JSON.stringify(resp.RequestId)
      );
      localStorage.setItem("interpreterSessionRoomId", JSON.stringify(roomKey));

      const GADATA = JSON.stringify({
        Language: interpreterSelectedLanguage.text,
        Selected_Gender: t(interpreterSelectedGender.text),
      });
      dispatch(interpreterInviteSend(GADATA));
      dispatch(setInterpreterRequestID(resp.RequestId));
      clearInfoNotification();
      showInterpreterInviteNotification();
      dispatch(setInterpreterRequestStatus(true));
    } else {
      dispatch(setInterpreterRequestStatus(false));
      const Message = resp?.errorMessage || "";
      retryInterpreterInviteNotification(Message);
    }
  };

  const completeInvite = async () => {
    if (interpreterCurrentSessionId.current === null) {
      return;
    }
    await interpreterProvider.current.completeInviteRequest(
      interpreterCurrentSessionId.current
    );
    dispatch(setInterpreterRequestStatus(false));
    dispatch(setInterpreterRequestID(null));
    clearInterval(pollingTimer.current);
    clearInfoNotification();
  };

  const clearInfoNotification = () => {
    clearInviteNotification();
    clearRetryNotification();
    clearGlobalRetryNotification();
  };

  const clearInviteNotification = () => {
    if (inviteNotification.current) {
      dismissNotification(inviteNotification.current);
      inviteNotification.current = null;
    }
  };
  const clearRetryNotification = () => {
    if (interpreterRetryInviteNotification.current) {
      dismissNotification(interpreterRetryInviteNotification.current);
      interpreterRetryInviteNotification.current = null;
    }
  };
  const clearGlobalRetryNotification = () => {
    if (window.interpreterRetryInviteNotification) {
      dismissNotification(window.interpreterRetryInviteNotification);
      window.interpreterRetryInviteNotification = null;
    }
  };

  const showInterpreterInviteNotification = () => {
    if (inviteNotification.current) {
      return;
    }
    clearRetryNotification();
    inviteNotification.current = showNotification("bannerWithBtns", {
      type: "banner",
      header: t("INTERPRETER_REQUEST_SENT"),
      message: (
        <div>
          <div className="message-content">
            <b>{`${t("LANGUAGE")}: `}</b>
            {`${interpreterSelectedLanguage.text}`}
          </div>
          {isEstimatedTimeAvailable && (
            <div className="estimate-time-interpreter"> {estimatedTime} </div>
          )}
        </div>
      ),
      className:
        "with-button cc-error-banner interpreter-invite-request-notification",
      showFor: -1,
      image: <img src={interpreter_icon} alt="" />,
      buttons: [
        {
          text: t("CANCEL_REQUEST"),
          className: "cancel-interpreter-request",
          onClick: () => {
            completeInvite();
          },
        },
        {
          text: "",
          className: "close-interpreter-request",
          onClick: () => {
            clearInfoNotification();
          },
        },
      ],
    });
  };

  const retryInterpreterInviteNotification = (message = "") => {
    if (interpreterRetryInviteNotification.current) {
      return;
    }
    clearInviteNotification();
    interpreterRetryInviteNotification.current = showNotification(
      "bannerWithBtns",
      {
        type: "banner",
        header: t("INTERPRETER_REQUEST_FAILED"),
        message:
          message !== "" ? (
            <div className="message-content">
              <div className="message-content">{message}</div>
              <div className="message-content">{`${t(
                "PLEASE_TRY_AGAIN"
              )}`}</div>
            </div>
          ) : (
            <div class="message-content">{t("PLEASE_TRY_AGAIN")}</div>
          ),
        className:
          "with-button cc-error-banner interpreter-invite-request-notification",
        image: <img src={interpreter_icon} alt="" />,
        showFor: -1,
        buttons: [
          {
            text: t("RETRY"),
            className: "retry-interpreter-request",
            onClick: () => {
              sendInvite();
            },
          },
          {
            text: "",
            className: "close-interpreter-request",
            onClick: () => {
              clearInfoNotification();
            },
          },
        ],
      }
    );
  };

  const getInterpreterRequestStatus = async () => {
    const resp = await interpreterProvider.current.getInviteStatus(
      interpreterCurrentSessionId.current
    );
    interpreRequestStatus.current = resp;
    dispatch(setInterpreterEstimateTime(resp?.estimatedTime || ""));
    //setEstimatedTime(resp?.estimatedTime || "");
    const element =
      document.getElementsByClassName("estimate-time-interpreter")[0] || null;
    if (element !== null) {
      element.innerHTML = resp?.estimatedTime || "";
    }
    if (resp.code === INTERPRETER_STATUS.CANCELLED) {
      clearInviteNotification();
      window.clearInterval(pollingTimer.current);
      dispatch(setInterpreterRequestStatus(false));
      pollingTimer.current = null;
      retryInterpreterInviteNotification();
      const GADATA = JSON.stringify({
        Language: interpreterSelectedLanguage.text,
        Selected_Gender: t(interpreterSelectedGender.text),
      });
      dispatch(interpreterInviteFail(GADATA));
    } else if (resp.code === INTERPRETER_STATUS.JOINED) {
      clearInfoNotification();
      dispatch(setInterpreterRequestStatus(false));
    } else if (resp.code === INTERPRETER_STATUS.COMPLETED) {
      clearInfoNotification();
      dispatch(setInterpreterRequestStatus(false));
      window.clearInterval(pollingTimer.current);
      pollingTimer.current = null;
    }
  };

  const startStatusPolling = () => {
    clearInterval(pollingTimer.current);
    pollingTimer.current = window.setInterval(
      getInterpreterRequestStatus,
      15000
    );
  };

  useEffect(() => {
    const participant = particpantList.participantLeft;
    if (
      participant?.name === interpreRequestStatus.current?.interpreterName &&
      participant?.userId === interpreRequestStatus.current?.interpreterId
    ) {
      interpreterLeftTimer.current = setTimeout(completeInvite, 6000);
    }
    // eslint-disable-next-line
  }, [particpantList.participantLeft]);

  useEffect(() => {
    const participant = particpantList.participantJoined;
    if (isInterpreterRequestInProgress) {
      setTimeout(getInterpreterRequestStatus, 1200);
    }

    if (
      participant?.name === interpreRequestStatus.current?.interpreterName &&
      participant?.userId === interpreRequestStatus.current?.interpreterId &&
      interpreterLeftTimer.current !== null
    ) {
      clearTimeout(interpreterLeftTimer.current);
      interpreterLeftTimer.current = null;
    }
    // eslint-disable-next-line
  }, [particpantList.participantJoined]);

  useEffect(() => {
    interpreterCurrentSessionId.current = interpreterSessionId;
    if (interpreterSessionId) {
      startStatusPolling(interpreterSessionId);
      clearInfoNotification();
      showInterpreterInviteNotification();
    } else if (interpreterSessionId === null && pollingTimer.current !== null) {
      clearInfoNotification();
      clearInterval(pollingTimer.current);
      pollingTimer.current = null;
    }
    // eslint-disable-next-line
  }, [interpreterSessionId]);

  useEffect(() => {
    if (
      !gcpServices?.interpreterService?.url ||
      !gcpServices?.interpreterService?.isServiceAvailable
    ) {
      console.error("listOfGCPServices closedCaption configuration is wrong");
    } else {
      /* setURL(
        "https://interpreterservicev2-116393635455.us-central1.run.app/api/v1"
      ); */
      setURL(gcpServices?.interpreterService?.url);
      interpreterProvider.current = getInterpreterProvider();
    }
    return () => {
      clearInfoNotification();
      clearInterval(pollingTimer.current);
    };
    // eslint-disable-next-line
  }, []);
};
export default useInterpreterPolling;
